 import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
 
 type ThemeStyle = 'liquid-glass' | 'normal';
 type ThemeColor = 'light' | 'dark';
 
 interface ThemeModeContextType {
   themeStyle: ThemeStyle;
   themeColor: ThemeColor;
   setThemeStyle: (style: ThemeStyle) => void;
   setThemeColor: (color: ThemeColor) => void;
   toggleThemeColor: () => void;
 }
 
 const ThemeModeContext = createContext<ThemeModeContextType | undefined>(undefined);
 
 const THEME_STYLE_KEY = 'theme-style';
 const THEME_COLOR_KEY = 'theme-color';

 // Track if liquid glass CSS has been loaded
 let liquidGlassLoaded = false;

 async function loadLiquidGlassCSS() {
   if (liquidGlassLoaded) return;
   await import('@/styles/liquid-glass.css');
   liquidGlassLoaded = true;
 }
 
 export function ThemeModeProvider({ children }: { children: ReactNode }) {
  const [themeStyle, setThemeStyleState] = useState<ThemeStyle>(() => {
      if (typeof window !== 'undefined') {
        const stored = localStorage.getItem(THEME_STYLE_KEY);
        // Brand mandate: Sunset Coral (normal) only — migrate away from the
        // legacy indigo liquid-glass theme, even if previously stored.
        if (stored === 'liquid-glass') {
          localStorage.setItem(THEME_STYLE_KEY, 'normal');
          return 'normal';
        }
        return (stored as ThemeStyle) || 'normal';
      }
      return 'normal';
    });
 
   const [themeColor, setThemeColorState] = useState<ThemeColor>(() => {
     if (typeof window !== 'undefined') {
       const stored = localStorage.getItem(THEME_COLOR_KEY);
       if (stored) return stored as ThemeColor;
       return 'light';
     }
     return 'light';
   });
 
   const setThemeStyle = (style: ThemeStyle) => {
     setThemeStyleState(style);
     localStorage.setItem(THEME_STYLE_KEY, style);
   };
 
   const setThemeColor = (color: ThemeColor) => {
     setThemeColorState(color);
     localStorage.setItem(THEME_COLOR_KEY, color);
   };
 
   const toggleThemeColor = () => {
     setThemeColor(themeColor === 'light' ? 'dark' : 'light');
   };
 
   useEffect(() => {
     const root = document.documentElement;
     
     // Set color mode class
     if (themeColor === 'dark') {
       root.classList.add('dark');
     } else {
       root.classList.remove('dark');
     }
     
     // Set theme style class
     root.classList.remove('theme-liquid-glass', 'theme-normal');
     root.classList.add(`theme-${themeStyle}`);

     // Dynamically load liquid glass CSS when needed
     if (themeStyle === 'liquid-glass') {
       loadLiquidGlassCSS();
     }
   }, [themeStyle, themeColor]);
 
   return (
     <ThemeModeContext.Provider
       value={{
         themeStyle,
         themeColor,
         setThemeStyle,
         setThemeColor,
         toggleThemeColor,
       }}
     >
       {children}
     </ThemeModeContext.Provider>
   );
 }
 
 export function useThemeMode() {
   const context = useContext(ThemeModeContext);
   if (!context) {
     throw new Error('useThemeMode must be used within a ThemeModeProvider');
   }
   return context;
 }
